import { useSelector, useDispatch } from "react-redux";
import { fromJS } from "immutable";
import GridTable from "../grid/GridTable";
import { useQuery } from "../AppLayout";
import { useMemo } from "react";
import TaskAction from "../actions/TaskAction";

const GridView = ({ gridData, onSearchTasks, currentSize, onChangeFilter, loading }) => {
  const dispatch = useDispatch();
  const contentType = useQuery().get("type") || "SALE";
  const projectViewId = useQuery().get("id") || "";

  const fields =
    useSelector((state: any) => state.projectReducer.getIn(["project", "fields"])) || fromJS([]);
  const settingColumns =
    useSelector((state: any) => state.grid.getIn(["viewSetting", contentType, "columns"])) ||
    fromJS([]);
  const gridFields = useSelector((state: any) => state.taskReducer.get("gridFields")) || fromJS([]);
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const isJira = tenantInfo?.getIn?.(["host", "product"]) === "JIRA";
  const fieldSorts = useSelector((state: any) => state.taskReducer.get("sorts"));

  const columns = useMemo(() => {
    if (gridFields.size > 0) {
      return isJira ? gridFields : gridFields.filter((item) => item.get("selector") !== "issue");
    }
    const fromViewSetting = settingColumns.filter((item) => item.get("show"));
    if (fromViewSetting.size > 0) {
      return isJira
        ? fromViewSetting
        : fromViewSetting.filter((item) => item.get("selector") !== "issue");
    }
    return fields
      .filter((f) => f.get("formType") === contentType && (isJira || f.get("id") !== "issue"))
      .map((f) => ({
        name: f.get("label"),
        selector: f.get("id"),
        width: f.get("id") === "name" ? 350 : 250,
        show: true,
        isCustomField: f.get("isCustomField"),
        formType: f.get("formType"),
        key: f.get("id"),
        id: f.get("id"),
      }));
  }, [gridFields, settingColumns, contentType, isJira, fields]);

  const hiddenColumns = useMemo(() => {
    const isShownField = (field) => {
      const filteredItem = columns.find((item) => item.get("selector") == field.get("id"));
      if (filteredItem) {
        return true;
      }
      return false;
    };
    return project
      .get("fields")
      .filter(
        (field) =>
          !isShownField(field) &&
          field.get("formType") == contentType &&
          (isJira || field.get("id") !== "issue")
      )
      .map((field) =>
        fromJS({
          name: field.get("label"),
          selector: field.get("id"),
          width: 250,
          show: false,
          isCustomField: field.get("isCustomField"),
          formType: field.get("formType"),
          key: field.get("id"),
          id: field.get("id"),
        })
      );
  }, [columns, contentType, isJira, project]);

  const getSortDirection = (fieldId) => {
    const sortedField =
      fieldSorts.find((item) => item.get("field") == `data.${fieldId}.value`) || fromJS({});
    const direction = sortedField.get("direction");
    if (direction && direction == "ASC") {
      return "DESC";
    }
    return "ASC";
  };

  const getCurrentSortDirection = (fieldId) => {
    const sortedField = fieldSorts.find((item) => item.get("field") == `data.${fieldId}.value`);
    return sortedField ? sortedField.get("direction") : null;
  };

  return (
    <GridTable
      currentSize={currentSize}
      heads={columns.toJS()}
      project={project}
      data={gridData}
      loading={loading}
      hiddenColumns={hiddenColumns}
      contentType={contentType}
      isSettings={projectViewId === ""}
      getSortDirection={getSortDirection}
      getCurrentSortDirection={getCurrentSortDirection}
      onChangeFilter={onChangeFilter}
      onReload={() => {
        const projectId = project.get("id");
        onSearchTasks({ projectId });
      }}
      onSortField={({ field, direction }) => {
        const projectId = project.get("id");
        const sorts = [{ field, direction }];
        onSearchTasks({ projectId, sorts });
      }}
      showHideColumn={(column) => {
        if (column.show) {
          dispatch(TaskAction.changeGridFields([...columns.toJS(), column]));
        } else {
          const newSortList = columns.toJS().filter((item) => item.selector !== column.selector);
          dispatch(TaskAction.changeGridFields([...newSortList]));
        }
      }}
      onResizeColumn={(column) => {
        const newSortList = columns
          .toJS()
          .map((item) =>
            item.selector === column.selector ? { ...item, width: column.width } : item
          );
        dispatch(TaskAction.changeGridFields([...newSortList]));
      }}
    />
  );
};

export default GridView;
